import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "summary",
    "studentId",
    "resourceId",
    "submitButton",
    "studentName",
    "resourceTitle",
    "resourceThumbnail",
    "resourcePreviewLink",
    "existingAssignmentWarning",
    "assignmentDetails",
    "studentForm",
    "resourceForm",
    "assignmentForm",
    "backLink",
  ];
  static values = {
    students: Array,
    resources: Array,
  };

  connect() {
    // Listen for changes in student form
    if (this.hasStudentFormTarget) {
      this.studentFormTarget.addEventListener(
        "change",
        this.handleStudentChange.bind(this)
      );
      this.studentFormTarget.addEventListener(
        "input",
        this.handleStudentChange.bind(this)
      );
    }

    // Listen for changes in resource form
    if (this.hasResourceFormTarget) {
      this.resourceFormTarget.addEventListener(
        "change",
        this.handleResourceChange.bind(this)
      );
      this.resourceFormTarget.addEventListener(
        "input",
        this.handleResourceChange.bind(this)
      );
    }

    // Listen for hotwire combobox specific events
    this.element.addEventListener(
      "hw-combobox:selection",
      this.handleComboboxSelection.bind(this)
    );

    // Listen for URL changes (like when "See more" is clicked)
    window.addEventListener("popstate", this.syncFormWithURL.bind(this));

    // Set initial state
    this.restoreFormState();
    this.updateSummary();
  }

  disconnect() {
    if (this.hasStudentFormTarget) {
      this.studentFormTarget.removeEventListener(
        "change",
        this.handleStudentChange.bind(this)
      );
      this.studentFormTarget.removeEventListener(
        "input",
        this.handleStudentChange.bind(this)
      );
    }
    if (this.hasResourceFormTarget) {
      this.resourceFormTarget.removeEventListener(
        "change",
        this.handleResourceChange.bind(this)
      );
      this.resourceFormTarget.removeEventListener(
        "input",
        this.handleResourceChange.bind(this)
      );
    }

    this.element.removeEventListener(
      "hw-combobox:selection",
      this.handleComboboxSelection.bind(this)
    );
    window.removeEventListener("popstate", this.syncFormWithURL.bind(this));
  }

  handleStudentChange(event) {
    if (event.target.name === "student_ids") {
      this.studentChanged(event);
    }
  }

  handleResourceChange(event) {
    if (event.target.name === "resource_id") {
      this.resourceChanged(event);
    }
  }

  handleComboboxSelection(event) {
    // console.log("Combobox selection event:", event.detail);
    // Check if this is the student combobox - now only check for student_ids
    if (event.detail.fieldName === "student_ids") {
      const studentIds = event.detail.value; // This will be comma-separated IDs like "7,6,15"

      // Update the hidden field with comma-separated IDs
      this.studentIdTarget.value = studentIds;

      // Update the display text to show selected student names
      this.updateStudentNamesDisplay(studentIds);

      this.updateSummary();
      this.updateURL();
    }
  }

  updateStudentNamesDisplay(studentIds) {
    if (!studentIds || studentIds === "") {
      this.studentNameTarget.textContent = "No student selected";
      return;
    }

    // Split comma-separated IDs and find corresponding student names
    const ids = studentIds.split(",").map((id) => id.trim());
    const studentNames = ids.map((id) => {
      const student = this.studentsValue.find(
        (s) => s.id == id || s.id === parseInt(id)
      );
      return student
        ? `${student.first_name} ${student.last_name}`
        : `Student ${id}`;
    });

    // Display as comma-separated list
    this.studentNameTarget.textContent = studentNames.join(", ");
  }

  studentChanged(event) {
    const studentId = event.target.value;

    // Try both string and number comparison
    const student = this.studentsValue.find(
      (s) => s.id == studentId || s.id === parseInt(studentId)
    );

    if (student) {
      this.studentNameTarget.textContent = `${student.first_name} ${student.last_name}`;
      this.studentIdTarget.value = studentId;
    } else {
      this.studentNameTarget.textContent = "No student selected";
      this.studentIdTarget.value = "";
    }

    this.updateSummary();
  }

  resourceChanged(event) {
    const resourceId = event.target.value;

    // Try both string and number comparison
    const resource = this.resourcesValue.find(
      (r) => r.id == resourceId || r.id === parseInt(resourceId)
    );

    if (resource) {
      this.resourceIdTarget.value = resource.id;
      this.resourceTitleTarget.textContent = resource.title;

      // Update thumbnail
      if (this.hasResourceThumbnailTarget && resource.thumbnail_url) {
        this.resourceThumbnailTarget.src = resource.thumbnail_url;
        this.resourceThumbnailTarget.alt = resource.title;
        this.resourceThumbnailTarget.classList.remove("hidden");
      }
    } else {
      this.resourceIdTarget.value = "";
      this.resourceTitleTarget.textContent = "No resource selected";

      // Hide thumbnail
      if (this.hasResourceThumbnailTarget) {
        this.resourceThumbnailTarget.classList.add("hidden");
      }
    }

    this.updateSummary();
    this.updateURL();
    this.updateBackLink();
  }

  restoreFormState() {
    // Get URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const studentIds = urlParams.get("student_ids");
    const resourceId = urlParams.get("resource_id");

    // Restore student selection (handle both single and multiple students)
    if (studentIds) {
      this.studentIdTarget.value = studentIds;
      this.updateStudentNamesDisplay(studentIds);
    }

    // Restore resource selection
    if (resourceId) {
      const resource = this.resourcesValue.find((r) => r.id == resourceId);
      if (resource) {
        this.resourceIdTarget.value = resourceId;
        this.resourceTitleTarget.textContent = resource.title;

        // Restore thumbnail
        if (this.hasResourceThumbnailTarget && resource.thumbnail_url) {
          this.resourceThumbnailTarget.src = resource.thumbnail_url;
          this.resourceThumbnailTarget.alt = resource.title;
          this.resourceThumbnailTarget.classList.remove("hidden");
        }

        // Check the corresponding radio button in the resource form
        const radioButton = this.resourceFormTarget?.querySelector(
          `input[name="resource_id"][value="${resourceId}"]`
        );
        if (radioButton) {
          radioButton.checked = true;
        }
      }
    }
  }

  updateSummary() {
    // Check if both required fields are filled
    const hasStudent =
      this.studentIdTarget.value !== "" && this.studentIdTarget.value !== null;
    const hasResource = this.resourceIdTarget.value !== "";
    const isFormValid = hasStudent && hasResource;

    // Update submit button state
    this.submitButtonTarget.disabled = !isFormValid;

    // Check for existing assignments and update warning
    this.checkExistingAssignments();

    // Update preview links with current form state
    this.updatePreviewLinks();
  }

  async checkExistingAssignments() {
    const studentIds = this.studentIdTarget.value;
    const resourceId = this.resourceIdTarget.value;

    // Clear existing warning
    this.clearExistingAssignmentWarning();

    // Only check if both student and resource are selected
    if (!studentIds || !resourceId) {
      return;
    }

    try {
      // Parse student IDs
      const studentIdArray = studentIds
        .split(",")
        .map((id) => id.trim())
        .filter((id) => id);

      if (studentIdArray.length === 0) {
        return;
      }

      // Check for existing assignments
      const response = await fetch(
        `/app/educators/schools/${this.getSchoolId()}/student_resource_assignments/check_existing`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
              .content,
            Accept: "application/json",
          },
          body: JSON.stringify({
            student_ids: studentIdArray,
            resource_id: resourceId,
          }),
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.existing_assignments && data.existing_assignments.length > 0) {
          this.showExistingAssignmentWarning(data.existing_assignments);
        }
      }
    } catch (error) {
      console.error("Error checking existing assignments:", error);
    }
  }

  showExistingAssignmentWarning(existingAssignments) {
    if (!this.hasExistingAssignmentWarningTarget) {
      return;
    }

    const formatDate = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      });
    };

    const formatDateCompact = (dateString) => {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      });
    };

    if (existingAssignments.length === 1) {
      const assignment = existingAssignments[0];
      const studentName = assignment.student_name;
      const assignedByName = assignment.assigned_by_name;
      const formattedDate = formatDate(assignment.created_at);

      this.existingAssignmentWarningTarget.innerHTML = `
        <div class="sm:mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <p class="text-sm text-yellow-800 font-medium text-pretty">
                ${assignedByName} already recommended this resource to ${studentName} on ${formattedDate}.
              </p>
              <p class="text-xs text-yellow-700 mt-1 text-pretty">If you want to recommend this resource again, you can still do so.</p>
            </div>
          </div>
        </div>
      `;
    } else {
      this.existingAssignmentWarningTarget.innerHTML = `
        <div class="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3 flex-1">
              <p class="text-sm text-yellow-800 font-medium text-pretty">
                ${
                  existingAssignments.length
                } students already have recommendations for this resource.
              </p>
              <p class="text-xs text-yellow-700 mt-1 text-pretty">If you want to assign this resource again, you can still do so.</p>
              
              <button 
                type="button"
                class="mt-2 text-xs text-yellow-600 hover:text-yellow-800 underline focus:outline-none cursor-pointer"
                data-action="click->student-resource-assignment-form#toggleAssignmentDetails"
              >
                View details
              </button>
              
              <div class="mt-2 hidden" data-student-resource-assignment-form-target="assignmentDetails">
                <div class="text-xs text-yellow-800 space-y-1 text-pretty">
                   ${existingAssignments
                     .map((assignment) => {
                       const formattedDate = formatDateCompact(
                         assignment.created_at
                       );
                       return `<div>• ${assignment.student_name} - by ${assignment.assigned_by_name} (${formattedDate})</div>`;
                     })
                     .join("")}
                </div>
              </div>
            </div>
          </div>
        </div>
      `;
    }
  }

  clearExistingAssignmentWarning() {
    if (this.hasExistingAssignmentWarningTarget) {
      this.existingAssignmentWarningTarget.innerHTML = "";
    }
  }

  toggleAssignmentDetails(event) {
    event.preventDefault();

    if (this.hasAssignmentDetailsTarget) {
      const detailsDiv = this.assignmentDetailsTarget;
      const button = event.target;

      if (detailsDiv.classList.contains("hidden")) {
        detailsDiv.classList.remove("hidden");
        button.textContent = "Hide details";
      } else {
        detailsDiv.classList.add("hidden");
        button.textContent = "View details";
      }
    }
  }

  getSchoolId() {
    // Extract school ID from the current URL
    const pathParts = window.location.pathname.split("/");
    const schoolIndex = pathParts.indexOf("schools");
    return schoolIndex !== -1 && schoolIndex + 1 < pathParts.length
      ? pathParts[schoolIndex + 1]
      : null;
  }

  updatePreviewLinks() {
    const studentIds = this.studentIdTarget.value;
    const resourceId = this.resourceIdTarget.value;

    // Update all preview links to include current form state
    this.resourcePreviewLinkTargets.forEach((link) => {
      const currentResourceId = link.dataset.resourceId;
      const url = new URL(link.href);

      // Add current student_ids if available
      if (studentIds) {
        url.searchParams.set("student_ids", studentIds);
      }

      // Add current resource_id if available
      if (resourceId) {
        url.searchParams.set("resource_id", resourceId);
      }

      link.href = url.toString();
    });
  }

  updateURL() {
    const studentIds = this.studentIdTarget.value;
    const resourceId = this.resourceIdTarget.value;

    // Update URL without page reload
    const url = new URL(window.location);

    if (studentIds) {
      url.searchParams.set("student_ids", studentIds);
    } else {
      url.searchParams.delete("student_ids");
    }

    if (resourceId) {
      url.searchParams.set("resource_id", resourceId);
    } else {
      url.searchParams.delete("resource_id");
    }

    // Update URL without triggering navigation
    window.history.replaceState({}, "", url.toString());
  }

  syncFormWithURL() {
    // Get current URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const resourceId = urlParams.get("resource_id");

    // Update resource selection if URL has resource_id
    if (resourceId) {
      const resource = this.resourcesValue.find((r) => r.id == resourceId);
      if (resource) {
        // Update the hidden field
        this.resourceIdTarget.value = resourceId;
        this.resourceTitleTarget.textContent = resource.title;

        // Update thumbnail
        if (this.hasResourceThumbnailTarget && resource.thumbnail_url) {
          this.resourceThumbnailTarget.src = resource.thumbnail_url;
          this.resourceThumbnailTarget.alt = resource.title;
          this.resourceThumbnailTarget.classList.remove("hidden");
        }

        // Check the corresponding radio button in the resource form
        const radioButton = this.resourceFormTarget?.querySelector(
          `input[name="resource_id"][value="${resourceId}"]`
        );
        if (radioButton) {
          radioButton.checked = true;
        }
      }
    } else {
      // If no resource_id in URL, uncheck all radio buttons
      const radioButtons = this.resourceFormTarget?.querySelectorAll(
        'input[name="resource_id"]'
      );
      radioButtons?.forEach((radio) => (radio.checked = false));
      this.resourceIdTarget.value = "";
      this.resourceTitleTarget.textContent = "No resource selected";

      // Hide thumbnail
      if (this.hasResourceThumbnailTarget) {
        this.resourceThumbnailTarget.classList.add("hidden");
      }
    }

    this.updateSummary();
  }

  updateBackLink() {
    if (!this.hasBackLinkTarget) {
      return;
    }

    const studentIds = this.studentIdTarget.value;
    const resourceId = this.resourceIdTarget.value;

    // Get the current URL path without query parameters
    const currentPath = window.location.pathname;
    const basePath = currentPath.replace(/\/new.*$/, "/new");

    // Build new URL with current form state
    const url = new URL(basePath, window.location.origin);

    if (studentIds) {
      url.searchParams.set("student_ids", studentIds);
    }
    if (resourceId) {
      url.searchParams.set("resource_id", resourceId);
    }

    // Update the back link href
    this.backLinkTarget.href = url.toString();
  }
}
